import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {CnCompareResultDto} from '../../../model/dto/api/cn-compare-result.dto';
import {CnComparisonYearRowDto} from '../../../model/dto/api/cn-comparison-year-row.dto';
import {CnBreakdownDto} from '../../../model/dto/api/cn-breakdown.dto';

/**
* Model-comparison result renderer (FR-12).
*
* Displays, for an active model vs. n scenario models over the same contract base:
*  - a per-year table: Summe(aktiv) vs. Summe(Szenario), Δ €, Δ %, nominal kumuliert AND NPV/Barwert,
*  - a bar chart of the per-year nominal series per model,
*  - per-product and per-Sparte breakdown tabs.
*
* All values are backend-computed and are based on expectedNetAmount (after cancellation),
* never on Brutto-Soll (NFR-CleanCore: NO calculation in the FE — display only).
*/
@Component({
    selector: 'mvs-cn-model-comparison-result',
    templateUrl: './cn-model-comparison-result.component.html',
    styleUrls: ['./cn-model-comparison-result.component.scss'],
    standalone: false
})
export class CnModelComparisonResultComponent implements OnInit, OnChanges, OnDestroy {

    /** Backend compare result (sync response or polled run result). */
    @Input() result: CnCompareResultDto | null;

    /** Optional human-readable model labels by id, for axis/table headers. */
    @Input() modelLabels: { [modelId: number]: string } = {};

    /** Rows of the active model (deltas are 0). */
    activeRows: CnComparisonYearRowDto[] = [];

    /** Scenario rows grouped by year for table rendering. */
    yearGroups: { year: number; rows: CnComparisonYearRowDto[] }[] = [];

    /** Chart.js data + options for the per-year nominal series. */
    chartData: any;
    chartOptions: any;

    /** Aggregated product / sparte breakdowns across all years. */
    productBreakdown: CnBreakdownDto[] = [];
    sparteBreakdown: CnBreakdownDto[] = [];

    activeTab: number = 0;
    initialized: boolean = false;

    ngOnInit(): void {
        this.initComponent();
    }

    /**
    * Initialize the result renderer: build the initial table / chart / breakdowns.
    */
    initComponent(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['result']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

    /**
    * Rebuild table groups, chart and breakdowns from the result DTO.
    */
    refreshComponent(): void {
        if (!this.result || !this.result.comparisonRows) {
            this.activeRows = [];
            this.yearGroups = [];
            this.chartData = null;
            this.productBreakdown = [];
            this.sparteBreakdown = [];
            return;
        }

        const rows = this.result.comparisonRows;
        const activeId = this.result.activeModelId;

        this.activeRows = rows.filter(r => r.modelId === activeId).sort((a, b) => a.year - b.year);

        // Group all rows by year for the comparison table.
        const byYear = new Map<number, CnComparisonYearRowDto[]>();
        for (const row of rows) {
            const list = byYear.get(row.year) ?? [];
            list.push(row);
            byYear.set(row.year, list);
        }
        this.yearGroups = Array.from(byYear.entries())
            .map(([year, yrRows]) => ({year, rows: yrRows}))
            .sort((a, b) => a.year - b.year);

        this.buildChart(rows, activeId);
        this.buildBreakdowns(rows);
    }

    /**
    * Build a grouped bar chart: one series per model (active + scenarios), x = year.
    */
    private buildChart(rows: CnComparisonYearRowDto[], activeId: number): void {
        const years = Array.from(new Set(rows.map(r => r.year))).sort((a, b) => a - b);
        const modelIds = Array.from(new Set(rows.map(r => r.modelId)));

        const datasets = modelIds.map(modelId => {
            const data = years.map(year => {
                const match = rows.find(r => r.modelId === modelId && r.year === year);
                return match ? match.nominalSum : 0;
            });
            return {
                label: this.getModelLabel(modelId) + (modelId === activeId ? ' (aktiv)' : ''),
                data
            };
        });

        this.chartData = {
            labels: years.map(y => String(y)),
            datasets
        };

        this.chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {legend: {position: 'bottom'}},
            scales: {y: {beginAtZero: true}}
        };
    }

    /**
    * Aggregate per-product and per-Sparte breakdowns across all years (active model rows).
    */
    private buildBreakdowns(rows: CnComparisonYearRowDto[]): void {
        const activeRows = rows.filter(r => r.modelId === this.result?.activeModelId);
        this.productBreakdown = this.aggregateBreakdown(activeRows.flatMap(r => r.byProduct ?? []));
        this.sparteBreakdown = this.aggregateBreakdown(activeRows.flatMap(r => r.bySparte ?? []));
    }

    /**
    * Sum breakdown rows by dimensionKey (backend values; no business calculation).
    */
    private aggregateBreakdown(items: CnBreakdownDto[]): CnBreakdownDto[] {
        const byKey = new Map<string, CnBreakdownDto>();
        for (const item of items) {
            const existing = byKey.get(item.dimensionKey);
            if (existing) {
                existing.nominalSum += item.nominalSum;
                existing.npvSum += item.npvSum;
            } else {
                byKey.set(item.dimensionKey, {...item});
            }
        }
        return Array.from(byKey.values());
    }

    /** Resolve a display label for a model id. */
    getModelLabel(modelId: number): string {
        return this.modelLabels[modelId] ?? ('Modell ' + modelId);
    }

    /** True when there is a result with rows to render. */
    hasResult(): boolean {
        return !!this.result && (this.result.comparisonRows?.length ?? 0) > 0;
    }

    /** Scenario rows for a given year (exclude active model). */
    scenarioRows(group: { year: number; rows: CnComparisonYearRowDto[] }): CnComparisonYearRowDto[] {
        return group.rows.filter(r => r.modelId !== this.result?.activeModelId);
    }

    /** Active row for a given year. */
    activeRow(group: { year: number; rows: CnComparisonYearRowDto[] }): CnComparisonYearRowDto | undefined {
        return group.rows.find(r => r.modelId === this.result?.activeModelId);
    }
}
